import { IReportHistory } from "@app/main/components/reports/models/IDynamicReportLog";
import { IVeriTableHeader, IVeriTableRow, IVeriTableRowCell } from "@directives/_veri/veriTable/veriTable.component";
import { IJourney } from "@interfaces/IJourney";
import { IPlot, IPlotEvent } from "@interfaces/IPlot";
import { IVehicle } from "@interfaces/IVehicle";
import { DynamicReportService, ExportType, IStandardColumns } from "@reports/_services/dynamicReports.service";
import { ColumnDataType } from "@reports/enums/ColumnDataType";
import { IColumnHeader } from "@reports/models/IColumnHeader";
import { IReportDefinition } from "@reports/models/IReportDefinition";
import { ReportParameterType } from "@reports/models/IReportParameters";
import { IReportRow } from "@reports/models/IReportRow";
import { IReportRowCell } from "@reports/models/IReportRowCell";
import { IReportTableDefinition } from "@reports/models/IReportTableDefinition";
import copy from "fast-copy/dist/fast-copy.esm.js";

export interface IExportTableParams {
    headers: IVeriTableHeader[];
    rows: IVeriTableRow[];

    exportType: ExportType;
    exportTitle: string;

    exportAsLandscape?: boolean;

    reportDefinition?: IReportDefinition;
}


export class LegacyExportService {
    static $inject = ["$q", "$mdDialog", "$window", "dynamicReportService"];

    constructor(
        private readonly $q: angular.IQService,
        private readonly $mdDialog: angular.material.IDialogService,
        private readonly $window: angular.IWindowService,
        private readonly dynamicReportService: DynamicReportService) {
    }

    showExportJourneysDialog(journeys: IJourney[], $event: MouseEvent, vehicle: IVehicle = null) {
        return this.$mdDialog.show(this.createDialogOptions($event, "journeysExport", journeys, vehicle));
    }

    showExportEventsDialog(events: IPlotEvent[], $event: MouseEvent) {
        return this.$mdDialog.show(this.createDialogOptions($event, "eventsExport", events));
    }

    showExportPlotsDialog(plots: IPlot[], $event: MouseEvent, vehicle: IVehicle = null) {
        return this.$mdDialog.show(this.createDialogOptions($event, "plotsExport", plots, vehicle));
    }

    downloadOldTableAsReport(params: IExportTableParams): angular.IPromise<any> {
        return this.createReportDefinitionForExport(params).then(reportDefinition => {
            reportDefinition.TableDefinition.Rows?.forEach(row => delete row.veriTableRow);
            return this.downloadReportExport(reportDefinition, params.exportType);
        });
    }
    
    downloadReport(type: ExportType, reportLog: IReportHistory): void {
        let documentUrl;

        switch (type) {
            case ExportType.CSV:
                documentUrl = reportLog.csvUrl;
                break;

            case ExportType.Excel:
                documentUrl = reportLog.excelUrl;
                break;

            case ExportType.PDF:
                documentUrl = reportLog.pdfUrl;
                break;

        }

        this.$window.open(documentUrl);
    }

    // VeriTable

    private createReportDefinitionForExport(params: IExportTableParams): angular.IPromise<IReportDefinition> {
        if (params.reportDefinition) {
            return this.$q.when(params.reportDefinition);
        }

        var headers: IVeriTableHeader[]
        var rows: IVeriTableRow[]

        if (params.headers && params.rows) {
            headers = params.headers;
            rows = params.rows;
        }

        if (!headers || !rows) {
            console.warn("No headers or rows to export");
            return this.$q.when(null);
        }

        return this.dynamicReportService.getStandardColumns().then(standardColumns => {

            return this.createReportTableFromVeriTable(headers, rows, standardColumns.stringColumn).then(tableDefinition => {

                var reportDefinition: IReportDefinition = {
                    Title: params.exportTitle,
                    TableDefinition: tableDefinition,
                    Parameters: {
                        CustomParameters: [
                            {
                                Value: true,
                                Active: true,
                                ParameterType: ReportParameterType.HideParameterList
                            }]
                    },
                    Portrait: !params.exportAsLandscape
                }

                return reportDefinition;
            });
        });
    }

    private createReportTableFromVeriTable(headers: IVeriTableHeader[], rows: IVeriTableRow[], stringColumn: IColumnHeader): angular.IPromise<IReportTableDefinition> {

        return this.createVeriTableReportRows(headers, rows, stringColumn).then(reportRows => {
            const report: IReportTableDefinition = {
                Headers: this.createVeriTableReportHeaders(headers, stringColumn),
                Rows: reportRows,
            };

            return report;
        });
    }

    private createVeriTableReportHeaders(headers: IVeriTableHeader[], stringColumn: IColumnHeader): IColumnHeader[] {
        const reportHeaders: IColumnHeader[] = [];

        headers.forEach((header: IVeriTableHeader) => {
            if (header.excludeFromExport) {
                return;
            }

            const reportHeader = copy(stringColumn);

            reportHeader.Title = header.title;
            reportHeader.Width = header.width ? header.width : undefined;

            reportHeaders.push(reportHeader);
        });

        return reportHeaders;
    }

    private createVeriTableReportRows(headers: IVeriTableHeader[], veriTableRows: IVeriTableRow[], stringColumn: IColumnHeader): angular.IPromise<IReportRow[]> {
        const rowPromises: angular.IPromise<any>[] = [];
        const reportRows: IReportRow[] = [];

        veriTableRows.forEach((row: IVeriTableRow, index) => {
            var rowPromise = this.createVeriTableReportCells(headers, row.cells, stringColumn).then(cells => {

                var reportRow: IReportRow = {
                    Parameters: null,
                    Cells: cells,
                    HasData: true,
                    veriTableRow: row
                };

                if (row.children) {
                    var subRowPromise = this.createVeriTableReportRows(headers, row.children, stringColumn).then(subRows => {

                        reportRow.ChildRows = subRows;
                    });

                    rowPromises.push(subRowPromise);
                }

                reportRows.push(reportRow);
            });

            rowPromises.push(rowPromise);
        });

        return this.$q.all(rowPromises).then(rows => {
            reportRows.sort((a, b) => {
                return veriTableRows.indexOf(a.veriTableRow) - veriTableRows.indexOf(b.veriTableRow);
            });

            return reportRows;
        });
    }

    private createVeriTableReportCells(headers: IVeriTableHeader[], cells: IVeriTableRowCell[], stringColumn: IColumnHeader): angular.IPromise<IReportRowCell[]> {
        const cellPromises: angular.IPromise<IReportRowCell>[] = [];

        cells.forEach((cell: IVeriTableRowCell, index) => {
            const header = headers[index];
            if (cell.excludeFromExport || header.excludeFromExport) {
                return;
            }

            cellPromises.push(this.createReportCellFromVeriTable(cell, stringColumn));
        });

        return this.$q.all(cellPromises).then(cells => {
            return cells;
        });
    }

    private createReportCellFromVeriTable(cell: IVeriTableRowCell, stringColumn: IColumnHeader): angular.IPromise<IReportRowCell> {

        const reportCell: IReportRowCell = {
            Data: cell.exportData || cell.contents || cell.data,
            Contents: cell.exportData || cell.contents || cell.data,
            CellDataType: ColumnDataType.String,
            DataSourceType: stringColumn.DataSourceType,
            DataSourceValue: stringColumn.DataSourceValue,
        };

        if (cell.exportDataMethod) {
            return cell.exportDataMethod().then(exportData => {
                reportCell.Data = exportData;
                reportCell.Contents = exportData;

                return reportCell;
            });
        }

        return this.$q.when(reportCell);
    }

    private downloadReportExport(reportDefinition: IReportDefinition, exportType: ExportType) {
        const documentUrl = this.getDocumentUrl(reportDefinition, exportType);

        if (documentUrl) {
            this.$window.open(documentUrl);
            return;
        }

        return this.dynamicReportService.generate(reportDefinition, exportType);
    }

    private getDocumentUrl(reportDefinition: IReportDefinition, exportType: ExportType) {
        switch (exportType) {
            case ExportType.PDF:
                return reportDefinition.reportPdfUrl;

            case ExportType.CSV:
                return reportDefinition.reportCsvUrl;

            case ExportType.Excel:
                return reportDefinition.reportExcelUrl;
        }
    }

    private createDialogOptions($event: MouseEvent, exportFileName: string, exportData: any, additionalData: any = null): angular.material.IDialogOptions {
        const controller = ($mdDialog) => {
            return {
                hide: (reload: boolean) => { $mdDialog.hide(reload); },
                exportData: exportData,
                additionalData: additionalData
            }
        };

        controller.$inject = ["$mdDialog"];

        return {
            targetEvent: $event,
            escapeToClose: true,
            clickOutsideToClose: true,
            template: require(`./${exportFileName}/${exportFileName}.dialog.html`),
            controller: controller,
            controllerAs: "$ctrl"
        }
    }
}
